import React, { ReactNode } from 'react';
import {
    Alert,
    Box,
    Button,
    Skeleton,
    Snackbar as MuiSnackbar,
    Stack,
    Typography,
} from '@mui/material';
import type { AlertColor, AlertProps, SnackbarOrigin } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { EmptyState } from '@mds/ui';

export interface SkeletonsProps {
    count?: number;
    dataLoaded?: boolean;
    className?: string;
    sx?: SxProps<Theme>;
    children?: ReactNode;
}

type AlertBoxStyleName = AlertColor | 'grey' | 'primary';

interface AlertBoxProps extends Omit<AlertProps, 'severity'> {
    visible?: boolean;
    styleName?: AlertBoxStyleName;
}

interface MessageViewAction {
    label: ReactNode;
    onClick: () => void;
    styleName?: AlertBoxStyleName;
}

interface MessageViewProps {
    title: string;
    children?: ReactNode;
    primaryAction?: MessageViewAction;
    secondaryAction?: MessageViewAction;
    styleName?: AlertBoxStyleName;
    size?: string;
    icon?: ReactNode;
}

type SnackbarCloseHandler = (
    event?: React.SyntheticEvent | Event,
    reason?: string
) => void;

interface SnackbarProps {
    open: boolean;
    message?: ReactNode;
    type?: AlertColor;
    anchorOrigin?: SnackbarOrigin;
    onClose?: SnackbarCloseHandler;
    onUndo?: () => void;
    undoLabel?: ReactNode;
}

const alertSeverity = (styleName: AlertBoxStyleName): AlertColor => {
    if (styleName === 'grey' || styleName === 'primary') return 'info';
    return styleName;
};

export const AlertBox = ({
    visible = true,
    styleName = 'info',
    children,
    onClose,
    ...props
}: AlertBoxProps) => {
    if (!visible) return null;

    return (
        <Alert severity={alertSeverity(styleName)} onClose={onClose} {...props}>
            {children}
        </Alert>
    );
};

export const MessageView = ({
    title,
    children,
    primaryAction,
    secondaryAction,
    styleName,
}: MessageViewProps) => (
    <Box sx={{ p: 3 }}>
        {styleName === 'error' ? (
            <Alert
                severity="error"
                sx={{
                    '& .MuiAlert-action': {
                        alignItems: 'center',
                        pt: 0,
                    },
                }}
                action={
                    <Stack direction="row" spacing={1}>
                        {secondaryAction && (
                            <Button
                                variant="text"
                                color="inherit"
                                size="small"
                                onClick={secondaryAction.onClick}
                            >
                                {secondaryAction.label}
                            </Button>
                        )}
                        {primaryAction && (
                            <Button
                                variant="text"
                                color="inherit"
                                size="small"
                                onClick={primaryAction.onClick}
                            >
                                {primaryAction.label}
                            </Button>
                        )}
                    </Stack>
                }
            >
                {title && <Typography variant="subtitle1">{title}</Typography>}
                {children}
            </Alert>
        ) : (
            <EmptyState
                // @mds/ui's EmptyStateProps intersects `title: ReactNode` with Stack's inherited
                // native `title?: string` DOM attribute instead of omitting it, which produces an
                // unsatisfiable type for any non-string title. Cast around the upstream type bug.
                title={title as any}
                subtitle={children}
                action={
                    <Stack direction="row" spacing={1}>
                        {secondaryAction && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={secondaryAction.onClick}
                            >
                                {secondaryAction.label}
                            </Button>
                        )}
                        {primaryAction && (
                            <Button
                                variant="contained"
                                onClick={primaryAction.onClick}
                            >
                                {primaryAction.label}
                            </Button>
                        )}
                    </Stack>
                }
            />
        )}
    </Box>
);

export const Snackbar = ({
    open,
    message,
    type = 'info',
    anchorOrigin,
    onClose,
    onUndo,
    undoLabel,
}: SnackbarProps) => (
    <MuiSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
    >
        <Alert
            onClose={onClose}
            severity={type}
            sx={{ minWidth: { xs: 'calc(100vw - 32px)', sm: 360 } }}
            action={
                onUndo ? (
                    <Button
                        variant="text"
                        color="inherit"
                        size="small"
                        onClick={onUndo}
                    >
                        {undoLabel ?? 'Undo'}
                    </Button>
                ) : undefined
            }
        >
            {message}
        </Alert>
    </MuiSnackbar>
);

export const Skeletons = ({
    count = 1,
    dataLoaded,
    className,
    sx,
    children,
}: SkeletonsProps) => {
    if (dataLoaded) return <>{children}</>;
    return (
        <>
            {Array.from({ length: count }).map((_, index) => (
                <Skeleton key={index} className={className} sx={sx} />
            ))}
        </>
    );
};
